<template>
  <div v-on="$listeners" class="shop-subcategory" :class="{ 'shop-subcategory--placeholder': placeholder }">
    <template v-if="placeholder">
      <div class="shop-subcategory__placeholder">
        <span>{{ $t('shopAddSubcategory') }}</span>
        <FontAwesomeIcon :icon="['fal', 'plus']" class="ml-1" />
      </div>
    </template>
    <template v-else>
      <div class="shop-subcategory__header">
        <div>
          <div>
            <FontAwesomeIcon v-if="hasError" class="shop-subcategory__validation-icon mr-1" icon="exclamation-circle" />
            <input
              type="text"
              v-model="value.label"
              :style="{ width: labelInputMinWidth }"
              class="shop-subcategory__label"
              :disabled="placeholder"
              :readonly="readonly"
              :placeholder="labelPlaceholder"
              @blur="visited = true"
            />
          </div>
          <div v-if="value.label">
            <input
              type="text"
              v-model="value.sublabel"
              :style="{ width: sublabelInputMinWidth }"
              class="shop-subcategory__sublabel"
              :disabled="placeholder"
              :readonly="readonly"
              :placeholder="sublabelPlaceholder"
              @blur="visited = true"
            />
          </div>
        </div>
        <div class="shop-subcategory__options ml-2">
          <Area
            v-if="hasAreaItems && !readonly"
            v-model="value.metadata.area"
            @track="() => shopTracker('SubcategoryArea')"
          />
          <Conditions v-if="!readonly" v-model="value.conditions" @track="() => shopTracker('SubcategoryConditions')" />
          <ButtonComponent v-if="!readonly" :icon="['fal', 'times']" :label="$t('delete')" @click="$emit('remove')" />
          <ButtonComponent
            v-if="!readonly"
            :icon="['fal', 'arrows-v']"
            class="shop-subcategory__options-handle-action handle"
          />
        </div>
      </div>
      <slot />
    </template>
  </div>
</template>

<script>
import ContentEditable from '@/components/ContentEditable'
import Conditions from '@/components/Shop/Conditions'
import Area from './Area'

export default {
  props: {
    value: {
      type: Object,
      default: () => ({}),
    },
    placeholder: Boolean,
    readonly: Boolean,
    categoryIndex: Number,
    index: Number,
  },
  inject: ['validator', 'shopTracker'],
  data() {
    return {
      visited: false,
    }
  },
  computed: {
    hasAreaItems() {
      return this.value.items.some(item => {
        return [this.$path('overrides.pricing.type', item), this.$path('product.pricing.type', item)].includes('area')
      })
    },
    hasError() {
      if (!this.visited) return
      let errors = Object.keys(this.validator.errors.errors)
      if (errors.includes(`categories.${this.categoryIndex}.categories.${this.index}.label`)) return true
      return
    },
    labelPlaceholder() {
      return `${this.$t('typeHere')}... (${this.$t('shopSubcategoryExamples')})`
    },
    sublabelPlaceholder() {
      return this.$t('shopSubcategorySublabelPlaceholder', { value: this.value.label })
    },
    labelInputMinWidth() {
      if (!this.value.label || !this.value.label.length) return this.labelPlaceholder.length + 'ch'
      return Math.max(5, this.value.label.length) + 'ch'
    },
    sublabelInputMinWidth() {
      if (!this.value.sublabel || !this.value.sublabel.length) return this.sublabelPlaceholder.length + 'ch'
      return Math.max(5, this.value.sublabel.length) + 'ch'
    },
  },
  methods: {
    onInput(ev) {
      this.visited = true
    },
  },
  components: {
    ContentEditable,
    Area,
    Conditions,
  },
}
</script>

<style lang="scss">
.shop-subcategory {
  transition: background-color 150ms ease-out;
  min-height: 150px;

  & + & {
    margin-top: 4rem;
  }

  &:not(.shop-subcategory--placeholder) {
    .shop-subcategory__label {
      font-weight: bold;
    }
  }

  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    border-bottom: 1px solid $border-color;
    padding-bottom: 1rem;
    margin-bottom: 1rem;
  }

  &__label {
    font-size: 1.2em;
    margin-right: auto;
    flex-grow: 1;

    @media screen and (max-width: 950px) {
      max-width: 450px;
      text-overflow: ellipsis;
      overflow: hidden;
      word-break: keep-all;
    }

    &:focus {
      outline: none;
    }
  }

  &__sublabel {
    font-size: 0.8em;
    margin-right: auto;
    flex-grow: 1;
    max-width: 650px;
    width: 100%;
    text-overflow: ellipsis;
    overflow: hidden;
    word-break: keep-all;

    @media screen and (max-width: 950px) {
      max-width: 450px;
    }

    &:focus {
      outline: none;
    }
  }

  &__validation-icon {
    color: var(--error);
  }

  &--placeholder {
    color: css-alpha('dark', 0.5);
    background-color: css-alpha('border-color', 0.2);
    border-radius: var(--border-radius);
    border: 1px dashed var(--border-color);
    cursor: pointer;
    display: flex;
    justify-content: center;
    align-items: center;
  }

  &__options {
    display: flex;
    flex-wrap: wrap;
    @include gap(0.5rem);
    justify-content: flex-end;
    &-handle-action {
      cursor: move;
    }
  }
}
</style>
